import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import QRCode from 'react-qr-code';
import './quickquizgame.css'; // Import the specific CSS file

const QuickQuizGame = ({ initialGuestName = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { game_id, topic_id } = useParams(); // Get both game_id and topic_id from URL
  const socketRef = useRef(null);
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const timerRef = useRef(null);

  // Get guest parameters from URL query if available
  const queryParams = new URLSearchParams(location.search);
  const isGuest = queryParams.get('guest') === 'true';
  const roomIdFromQuery = queryParams.get('room');
  const topicIdFromQuery = queryParams.get('topic');

  // Game state
  const [connected, setConnected] = useState(false);
  const [players, setPlayers] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [selectedAnswerId, setSelectedAnswerId] = useState(null); // Add for option ID tracking
  const [gameStarted, setGameStarted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [results, setResults] = useState([]);
  const [message, setMessage] = useState('');
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [score, setScore] = useState(0);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [explanation, setExplanation] = useState('');
  const [countdown, setCountdown] = useState(null);
  
  // Topic state
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(topicIdFromQuery || topic_id || '');
  const [fetchingTopics, setFetchingTopics] = useState(false);
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(120); // 2 minutes by default
  const [timerRunning, setTimerRunning] = useState(false);
  const [guestName, setGuestName] = useState(initialGuestName || '');
  const [showGuestInput, setShowGuestInput] = useState(isGuest && !initialGuestName);
  const [player_id, setPlayerId] = useState('');
  const [player_name, setPlayerName] = useState('');

  // Add new state for the QR code modal
  const [showQRCode, setShowQRCode] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [guestUrl, setGuestUrl] = useState('');
  const [activeTab, setActiveTab] = useState('guest'); // Move this state up to the component level

  // Add state to track players who have submitted answers
  const [submittedPlayers, setSubmittedPlayers] = useState([]);

  useEffect(() => {
    // Connect to WebSocket when component mounts
    connectWebSocket();
    
    // Fetch available topics
    fetchTopics();

    // If coming in as a guest with room ID or has initialGuestName, show guest input or join automatically
    if (isGuest && roomIdFromQuery) {
      if (initialGuestName) {
        // Auto-join if we have a guest name
        setGuestName(initialGuestName);
      } else {
        // Show guest input if no name provided
        setShowGuestInput(true);
      }
    }

    // Clean up when component unmounts
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Effect to auto-join when guestName is set from initialGuestName
  useEffect(() => {
    // If connected and we have a guest name from props, join the game
    if (connected && initialGuestName && !players.some(p => p.name === initialGuestName)) {
      joinGame();
    }
  }, [connected, initialGuestName]);
  
  // Fetch topics from API
  const fetchTopics = async () => {
    try {
      setFetchingTopics(true);
      const response = await axios.get('https://api.andrewslearning.com/api/topics/');
      console.log("Fetched topics:", response.data);
      setTopics(response.data);
      
      // If topic_id is provided in URL and exists in fetched topics, select it
      const topicToUse = topicIdFromQuery || topic_id;
      if (topicToUse && response.data.some(topic => topic.id.toString() === topicToUse)) {
        setSelectedTopic(topicToUse);
      }
    } catch (error) {
      console.error('Error fetching topics:', error);
      setMessage('Failed to load topics. Please try again later.');
    } finally {
      setFetchingTopics(false);
    }
  };

  // Timer effect
  useEffect(() => {
    if (timerRunning && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prevTime => {
          if (prevTime <= 1) {
            // Time's up, end the game
            clearInterval(timerRef.current);
            setTimerRunning(false);
            endGame();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    } else if (timeRemaining === 0 && timerRunning) {
      setTimerRunning(false);
      clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timerRunning, timeRemaining]);

  const connectWebSocket = () => {
    // Use the actual game_id and topic_id from URL params, query params, or use defaults
    const gameRoomId = roomIdFromQuery || game_id || `room_${Math.floor(Math.random() * 10000)}`;
    
    // Create WebSocket connection
    let wsUrl = `wss://api.andrewslearning.com/wschat/quickquiz/${gameRoomId}/`;
    
    // Add topic parameter if selected
    if (selectedTopic) {
      wsUrl += `?topic=${selectedTopic}`;
    }
    
    // Close existing connection if any
    if (socketRef.current) {
      socketRef.current.close();
    }
    
    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = () => {
      console.log('WebSocket connection established');
      setConnected(true);
      setMessage('Connected to game server');
      
      // If we're a guest with a roomID and guest name, let's join automatically after connecting
      if ((isGuest && roomIdFromQuery && guestName) || initialGuestName) {
        joinGame();
      }
    };

    socketRef.current.onclose = (event) => {
      console.log('WebSocket connection closed', event);
      setConnected(false);
      setMessage('Disconnected from game server');
    };

    socketRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      setMessage('Error connecting to game server');
    };

    socketRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Received message:', data);
      
      handleSocketMessage(data);
    };
  };

  // Reconnect WebSocket when topic changes
  useEffect(() => {
    if (selectedTopic !== topic_id && !gameStarted) {
      connectWebSocket();
    }
  }, [selectedTopic]);

  const handleSocketMessage = (data) => {
    switch (data.type) {
      case 'welcome_message':
        setMessage(data.message);
        break;
        
      case 'player_list':
      case 'player_list_update':
        setPlayers(data.players || []);
        break;
        
      case 'join_success':
        setMessage(`Joined as ${data.player_name}`);
        setPlayerId(data.player_id);
        setPlayerName(data.player_name);
        break;
        
      case 'player_joined':
        setMessage(`${data.player_name} joined the game`);
        break;
        
      case 'player_left':
        setMessage(`${data.player_name} left the game`);
        break;
        
      case 'game_started':
        setGameStarted(true);
        setMessage('Game started!');
        // Start the timer when game starts
        setTimerRunning(true);
        break;
        
      case 'new_question':
        setCurrentQuestion(data.question);
        setQuestionNumber(data.question_number);
        setTotalQuestions(data.total_questions);
        setSelectedAnswer('');
        setSelectedAnswerId(null);
        setCorrectAnswer(null);
        setShowFeedback(false);
        setExplanation('');
        // Clear submitted players when a new question is shown
        setSubmittedPlayers([]);
        break;
        
      case 'answer_result':
        setCorrectAnswer(data.correct_answer);
        setScore(data.player_score || 0);
        setShowFeedback(true);
        
        // If explanation is provided
        if (data.explanation) {
          setExplanation(data.explanation);
        }
        
        // If answer was correct
        if (data.is_correct) {
          setMessage('Correct answer!');
        } else {
          setMessage(`Incorrect. The correct answer is: ${data.correct_answer}`);
        }
        break;
        
      case 'game_completed':
        // User has completed all questions
        setMessage(`You've completed all questions! Your score: ${data.player_score}`);
        break;
        
      case 'game_over':
        setGameEnded(true);
        setGameStarted(false);
        setTimerRunning(false);
        
        // Format the results for display
        const formattedResults = Array.isArray(data.final_scores) 
          ? data.final_scores 
          : Object.entries(data.final_scores || {}).map(([name, score]) => ({ name, score }));
        
        setResults(formattedResults);
        setMessage('Game over!');
        break;
        
      case 'debug':
        console.log('Debug message:', data.message);
        break;
        
      case 'error':
        setMessage(`Error: ${data.message}`);
        break;
        
      case 'player_submitted':
        // Track players who've submitted answers
        setSubmittedPlayers(prev => {
          if (!prev.includes(data.player_id)) {
            return [...prev, data.player_id];
          }
          return prev;
        });
        break;
        
      case 'score_update':
        // Update the score of a player in the players array
        setPlayers(prevPlayers => {
          return prevPlayers.map(player => {
            if (player.id === data.player_id) {
              return { ...player, score: data.score };
            }
            return player;
          });
        });
        
        // Show a toast or notification for score changes
        if (data.player_id !== player_id) {
          setMessage(`${data.player_name} scored a point!`);
        }
        break;
        
      default:
        console.log('Unhandled message type:', data.type);
    }
  };

  const joinGame = () => {
    if (!connected) {
      setMessage("Not connected to game server. Please try again.");
      return;
    }
    
    // If no user is logged in and no guest name is provided, show the guest input
    if (!userInfo && !guestName) {
      setShowGuestInput(true);
      return;
    }
    
    const payload = {
      type: 'join_game',
      player_id: userInfo ? userInfo.id : `guest_${Date.now()}`,
      player_name: userInfo ? userInfo.username : guestName,
      topic_id: selectedTopic // Include selected topic
    };
    
    console.log("Joining game with payload:", payload);
    socketRef.current.send(JSON.stringify(payload));
    setShowGuestInput(false);
  };
  
  const startGame = () => {
    if (!connected) return;
    
    // Start the countdown before the actual game
    startCountdown().then(() => {
      const payload = {
        type: 'start_game',
        topic_id: selectedTopic // Include selected topic
      };
      
      socketRef.current.send(JSON.stringify(payload));
    });
  };
  
  const startCountdown = () => {
    return new Promise((resolve) => {
      let timer = 3;
      const finishMessage = "BEGIN!";
      setCountdown(timer);
  
      const interval = setInterval(() => {
        timer -= 1;
        setCountdown(timer > 0 ? timer : finishMessage);
  
        if (timer <= 0) {
          clearInterval(interval);
          console.log("Countdown finished!");
  
          // Show "BEGIN!" for 1 second before clearing
          setTimeout(() => {
            setCountdown(null);  // Clear after delay
            resolve();  // Notify that the countdown is complete after clearing
          }, 1000);
        }
      }, 1000);
    });
  };
  
  const submitAnswer = () => {
    if (!connected || !currentQuestion || (!selectedAnswer && !selectedAnswerId)) return;
    
    // First, notify others that you've submitted (without revealing your answer)
    const submissionNotice = {
      type: 'player_submitted',
      player_id: player_id,
      player_name: player_name
    };
    socketRef.current.send(JSON.stringify(submissionNotice));
    
    // Then send the actual answer after a short delay
    setTimeout(() => {
      const payload = {
        type: 'submit_answer',
        question_id: currentQuestion.id,
        answer: selectedAnswer,
        answer_id: selectedAnswerId
      };
      socketRef.current.send(JSON.stringify(payload));
    }, 100);
  };
  
  const requestNextQuestion = () => {
    if (!connected) return;
    
    const payload = {
      type: 'request_next_question',
    };
    
    socketRef.current.send(JSON.stringify(payload));
    setShowFeedback(false);
  };
  
  const endGame = () => {
    if (!connected) return;
    
    const payload = {
      type: 'end_game',
    };
    
    socketRef.current.send(JSON.stringify(payload));
    setTimerRunning(false);
  };

  const handleAnswerSelection = (answerText, answerId) => {
    setSelectedAnswer(answerText);
    setSelectedAnswerId(answerId);
  };
  
  const handleGuestNameChange = (e) => {
    setGuestName(e.target.value);
  };
  
  const toggleGuestInput = () => {
    setShowGuestInput(!showGuestInput);
  };
  
  const handleTopicChange = (e) => {
    const newTopicId = e.target.value;
    console.log("Topic changed to:", newTopicId);
    console.log("Selected topic name:", e.target.options[e.target.selectedIndex].text);
    setSelectedTopic(newTopicId);
  };

  // Function to generate share URL
  const generateShareUrl = () => {
    // Use the current room ID from params or generate a new one
    const roomId = game_id || roomIdFromQuery || Math.floor(Math.random() * 10000);
    const topicParam = selectedTopic ? `/${selectedTopic}` : '';
    const url = `${window.location.origin}/quickquiz/${roomId}${topicParam}`;
    return url;
  };
  
  // Function to generate guest URL
  const generateGuestUrl = () => {
    // Use the current room ID from params or generate a new one
    const roomId = game_id || roomIdFromQuery || Math.floor(Math.random() * 10000);
    const topicParam = selectedTopic ? `/${selectedTopic}` : '';
    const url = `${window.location.origin}/quickquiz-invite/${roomId}${topicParam}`;
    return url;
  };
  
  // Function to handle sharing
  const handleShare = () => {
    const hostUrl = generateShareUrl();
    const guestUrl = generateGuestUrl();
    setShareUrl(hostUrl);
    setGuestUrl(guestUrl);
    navigator.clipboard.writeText(guestUrl);
    setShowQRCode(true);
    setMessage('Guest link copied to clipboard! Share with friends to play together.');
  };
  
  const renderQuestion = () => {
    if (!currentQuestion) return null;
    
    return (
      <div className="question-container">
        <h3>Question {questionNumber} of {totalQuestions}</h3>
        <div className="question">
          {currentQuestion.image && (
            <img 
              src={currentQuestion.image} 
              alt="Question" 
              className="question-image" 
            />
          )}
          <p>{currentQuestion.text}</p>
          
          <div className="choices">
            {currentQuestion.options && currentQuestion.options.map((option, index) => (
              <div 
                key={option.id || index} 
                className={`choice ${selectedAnswerId === option.id ? 'selected' : ''} ${
                  showFeedback && correctAnswer === option.text ? 'correct' : ''
                } ${
                  showFeedback && selectedAnswerId === option.id && correctAnswer !== option.text ? 'incorrect' : ''
                }`}
                onClick={() => !showFeedback && handleAnswerSelection(option.text, option.id)}
              >
                {option.image && (
                  <img src={option.image} alt={option.text} className="choice-image" />
                )}
                <span>{option.text}</span>
              </div>
            ))}
          </div>
          
          {showFeedback && explanation && (
            <div className="explanation">
              <h4>Explanation:</h4>
              <p>{explanation}</p>
            </div>
          )}
          
          {!showFeedback ? (
            <button 
              className="submit-btn" 
              onClick={submitAnswer}
              disabled={!selectedAnswer && !selectedAnswerId}
            >
              Submit Answer
            </button>
          ) : (
            <button 
              className="next-btn" 
              onClick={requestNextQuestion}
            >
              Next Question
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderPlayerList = () => {
    const isCurrentPlayer = (playerName) => {
      if (userInfo) return playerName === userInfo.username;
      return guestName && playerName === guestName;
    };
    
    // Sort players by score for ranking
    const sortedPlayers = [...players].sort((a, b) => (b.score || 0) - (a.score || 0));
    
    return (
      <div className="player-list">
        <h3>Leaderboard</h3>
        {players.length === 0 ? (
          <p style={{color: '#666', fontStyle: 'italic', textAlign: 'center'}}>
            No players have joined yet
          </p>
        ) : (
          <div className="player-rankings">
            {sortedPlayers.map((player, index) => {
              const isCurrentUser = isCurrentPlayer(player.name);
              const hasSubmitted = submittedPlayers && submittedPlayers.includes(player.id);
              
              return (
                <div 
                  key={index} 
                  className={`player-ranking-item ${isCurrentUser ? 'current-player' : ''} ${hasSubmitted ? 'submitted' : ''}`}
                >
                  <div className="rank-indicator">{index + 1}</div>
                  <div className="player-info">
                    <span className="player-name">
                      {isCurrentUser ? '→ ' : ''}
                      {player.name} 
                      {player.name.includes('Guest_') && ' (Guest)'}
                    </span>
                    {hasSubmitted && (
                      <span className="submission-status">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 11L12 14L22 4" stroke="#2ecc71" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="#2ecc71" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    )}
                  </div>
                  <div className={`player-score ${player.score > 0 ? 'has-points' : ''}`}>
                    {player.score || 0}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderGameResults = () => {
    if (!gameEnded || !results || results.length === 0) return null;
    
    // Find the current topic name
    const currentTopicName = topics.find(t => t.id.toString() === selectedTopic)?.name || 'Selected Topic';
    
    return (
      <div className="game-results">
        <h2>Game Results</h2>
        <h4>Topic: {currentTopicName}</h4>
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Player</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {results.map((player, index) => (
              <tr key={index} className={index === 0 ? 'winner' : ''}>
                <td>{index + 1}</td>
                <td>{player.player_name || player.name}</td>
                <td>{player.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        <button className="restart-btn" onClick={() => window.location.reload()}>
          Play Again
        </button>
      </div>
    );
  };

  const renderTopicSelector = () => {
    if (gameStarted || fetchingTopics) return null;
    
    const selectStyles = {
      padding: '10px 15px',
      borderRadius: '5px',
      border: '2px solid #dee2e6',
      backgroundColor: 'white',
      color: 'black',
      fontSize: '16px',
      cursor: 'pointer',
      minWidth: '200px',
      marginLeft: '10px'
    };
    
    const labelStyles = {
      fontWeight: '600',
      color: 'black',
      fontSize: '16px'
    };
    
    const containerStyles = {
      marginBottom: '15px',
      display: 'flex',
      alignItems: 'center',
      padding: '10px',
      backgroundColor: '#f0f0f0',
      borderRadius: '8px'
    };
    
    return (
      <div style={containerStyles}>
        <label htmlFor="topic-select" style={labelStyles}>Select Topic:</label>
        <select 
          id="topic-select"
          value={selectedTopic}
          onChange={handleTopicChange}
          disabled={gameStarted}
          style={selectStyles}
        >
          <option value="" style={{color: 'black', backgroundColor: 'white'}}>All Topics</option>
          {topics.length > 0 ? (
            topics.map(topic => (
              <option key={topic.id} value={topic.id} style={{color: 'black', backgroundColor: 'white'}}>
                {topic.title}
              </option>
            ))
          ) : (
            <option disabled style={{color: 'gray', backgroundColor: 'white'}}>Loading topics...</option>
          )}
        </select>
      </div>
    );
  };

  const renderGameControls = () => {
    if (!connected) return null;
    
    if (!gameStarted && !gameEnded) {
      const buttonStyles = {
        backgroundColor: '#3498db',
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: 'bold',
        marginTop: '10px',
        transition: 'background-color 0.3s'
      };
      
      return (
        <div className="game-controls">
          {renderTopicSelector()}
          
          {/* Guest or user joining */}
          {showGuestInput ? (
            renderGuestInput()
          ) : (
            <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
              {!userInfo ? (
                <button 
                  onClick={toggleGuestInput}
                  style={buttonStyles}
                >
                  Play as Guest
                </button>
              ) : (
                <button 
                  onClick={joinGame}
                  style={buttonStyles}
                >
                  Join as {userInfo.username}
                </button>
              )}
              
              {/* Share link option */}
              <button
                onClick={handleShare}
                style={{...buttonStyles, backgroundColor: '#27ae60'}}
              >
                Share Game Link
              </button>
            </div>
          )}
          
          {/* Start game button - only show when at least one player has joined */}
          {players.length > 0 && (
            <div style={{marginTop: '15px', width: '100%'}}>
              <button 
                onClick={startGame}
                style={{
                  ...buttonStyles,
                  backgroundColor: '#f39c12',
                  width: '100%',
                  padding: '12px 0',
                  fontSize: '18px'
                }}
              >
                Start Game ({players.length} {players.length === 1 ? 'player' : 'players'})
              </button>
            </div>
          )}
          
          <div className="game-settings">
            <label>
              Time Limit:
              <select 
                value={timeRemaining} 
                onChange={(e) => setTimeRemaining(parseInt(e.target.value))}
                disabled={gameStarted || timerRunning}
                style={{
                  marginLeft: '10px',
                  padding: '8px',
                  borderRadius: '5px',
                  border: '1px solid #ddd'
                }}
              >
                <option value="60">1 Minute</option>
                <option value="120">2 Minutes</option>
                <option value="180">3 Minutes</option>
                <option value="300">5 Minutes</option>
              </select>
            </label>
          </div>
        </div>
      );
    }
    
    if (gameStarted && !gameEnded) {
      return (
        <div className="game-controls">
          <button onClick={endGame} className="end-game-btn">End Game</button>
          <div className="timer-display">
            Time: {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
          </div>
          <div className="score-display">
            Score: {score}
          </div>
        </div>
      );
    }
    
    return null;
  };

  const renderGuestInput = () => {
    if (!showGuestInput) return null;
    
    const inputStyles = {
      padding: '10px 15px',
      borderRadius: '5px',
      border: '2px solid #dee2e6',
      fontSize: '16px',
      width: '100%',
      marginBottom: '10px'
    };
    
    const buttonStyles = {
      backgroundColor: '#3498db',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '5px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
      transition: 'background-color 0.3s'
    };
    
    const containerStyles = {
      backgroundColor: '#f8f9fa',
      padding: '15px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      marginTop: '10px',
      marginBottom: '15px'
    };
    
    return (
      <div style={containerStyles}>
        <h3 style={{marginTop: 0, marginBottom: '10px'}}>Join as Guest</h3>
        <input
          type="text"
          placeholder="Enter your name"
          value={guestName}
          onChange={handleGuestNameChange}
          style={inputStyles}
          maxLength={20}
        />
        <div style={{display: 'flex', gap: '10px'}}>
          <button 
            onClick={joinGame} 
            style={buttonStyles}
            disabled={!guestName.trim()}
          >
            Join Game
          </button>
          <button 
            onClick={() => setShowGuestInput(false)} 
            style={{...buttonStyles, backgroundColor: '#e74c3c'}}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // QR Code Modal Component
  const renderQRCodeModal = () => {
    if (!showQRCode) return null;
    
    const modalOverlayStyles = {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    };
    
    const modalContentStyles = {
      backgroundColor: 'white',
      padding: '30px',
      borderRadius: '10px',
      maxWidth: '90%',
      width: '400px',
      textAlign: 'center',
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
      position: 'relative'
    };
    
    const closeButtonStyles = {
      position: 'absolute',
      top: '10px',
      right: '10px',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#777'
    };
    
    const buttonStyles = {
      backgroundColor: '#3498db',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '5px',
      cursor: 'pointer',
      fontSize: '16px',
      marginTop: '20px',
      fontWeight: 'bold'
    };
    
    const qrWrapperStyles = {
      backgroundColor: 'white',
      padding: '15px',
      borderRadius: '5px',
      display: 'inline-block',
      margin: '15px 0'
    };
    
    const urlDisplayStyles = {
      padding: '8px',
      backgroundColor: '#f8f9fa',
      borderRadius: '5px',
      fontSize: '12px',
      wordBreak: 'break-all',
      marginTop: '15px',
      border: '1px solid #ddd'
    };
    
    const tabStyles = {
      display: 'flex',
      borderBottom: '1px solid #dee2e6',
      marginBottom: '20px'
    };
    
    const tabButtonStyles = (active) => ({
      padding: '10px 15px',
      backgroundColor: active ? '#3498db' : '#f8f9fa',
      color: active ? 'white' : '#495057',
      border: 'none',
      borderRadius: '5px 5px 0 0',
      cursor: 'pointer',
      fontWeight: active ? 'bold' : 'normal',
      flex: 1
    });
    
    // Get the current URL based on the active tab
    const currentUrl = activeTab === 'guest' ? guestUrl : shareUrl;
    
    return (
      <div style={modalOverlayStyles} onClick={() => setShowQRCode(false)}>
        <div style={modalContentStyles} onClick={(e) => e.stopPropagation()}>
          <button 
            style={closeButtonStyles} 
            onClick={() => setShowQRCode(false)}
          >
            ×
          </button>
          <h3 style={{margin: '0 0 15px'}}>Share Game</h3>
          
          <div style={tabStyles}>
            <button 
              style={tabButtonStyles(activeTab === 'guest')}
              onClick={() => setActiveTab('guest')}
            >
              Guest Link
            </button>
            <button 
              style={tabButtonStyles(activeTab === 'host')}
              onClick={() => setActiveTab('host')}
            >
              Host Link
            </button>
          </div>
          
          <p style={{margin: '0 0 15px'}}>
            {activeTab === 'guest' 
              ? 'Share this link with guests to join your game:' 
              : 'Use this link to host the same game:'}
          </p>
          
          <div style={qrWrapperStyles}>
            <QRCode 
              value={currentUrl} 
              size={200}
              level="M"
              fgColor="#000"
              bgColor="#fff"
            />
          </div>
          
          <div style={urlDisplayStyles}>
            {currentUrl}
          </div>
          
          <div style={{marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center'}}>
            <button 
              style={buttonStyles}
              onClick={() => {
                navigator.clipboard.writeText(currentUrl);
                setMessage(`${activeTab === 'guest' ? 'Guest' : 'Host'} link copied to clipboard!`);
              }}
            >
              Copy Link
            </button>
            <button 
              style={{...buttonStyles, backgroundColor: '#e74c3c'}}
              onClick={() => setShowQRCode(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="quick-quiz-container">
      <h1>Quick Quiz Game</h1>
      
      {countdown && (
        <div className="countdown-overlay">
          <div className="countdown">{countdown}</div>
        </div>
      )}
      
      {message && (
        <div className="message">
          {message}
        </div>
      )}
      
      <div className="game-area">
        {renderGameControls()}
        
        <div className="main-content">
          {gameStarted && !gameEnded && renderQuestion()}
          {gameEnded && renderGameResults()}
        </div>
        
        {renderPlayerList()}
      </div>
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      {renderQRCodeModal()}
    </div>
  );
};

export default QuickQuizGame; 